import type { UserRole } from "../types.js";

/**
 * Configuração de navegação (Design.md §4.1) — ordem normativa e visibilidade
 * por papel. Lógica PURA (sem React/DOM) para ser testável em node.
 *
 * Visibilidade por papel (decisão 5 do WEBUI-PLAN.md): Fornecedores e Usuários
 * são ADMIN-only. Para OPERADOR, são ocultos no menu (e as rotas têm guard).
 * Configurações NÃO consta no menu §4.1 — entra numa iteração posterior.
 */
export type NavItemId =
  | "gerenciar"
  | "criar"
  | "inventario"
  | "fornecedores"
  | "prep-center"
  | "usuarios"
  | "relatorio";

export interface NavItem {
  id: NavItemId;
  label: string;
  path: string;
  /** Quando true, só ADMIN vê o item (e a rota é guardada). */
  adminOnly?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: "operacao",
    label: "Operação",
    items: [
      { id: "gerenciar", label: "Gerenciar envios", path: "/" },
      { id: "criar", label: "Criar envios", path: "/criar" },
      { id: "inventario", label: "Inventário", path: "/inventario" }
    ]
  },
  {
    id: "cadastro",
    label: "Cadastro",
    items: [
      {
        id: "fornecedores",
        label: "Fornecedores",
        path: "/fornecedores",
        adminOnly: true
      },
      { id: "prep-center", label: "Prep Center", path: "/prep-center" },
      { id: "usuarios", label: "Usuários", path: "/usuarios", adminOnly: true }
    ]
  },
  {
    id: "analise",
    label: "Análise",
    items: [{ id: "relatorio", label: "Relatório", path: "/relatorio" }]
  }
];

/** Grupos/itens visíveis para o papel — colapsa grupos que ficam vazios. */
export function visibleNavForRole(role: UserRole): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => role === "ADMIN" || item.adminOnly !== true
    )
  })).filter((group) => group.items.length > 0);
}

const ALL_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

/** Rotas fora do menu §4.1 (ex.: Configurações via engrenagem no cabeçalho). */
const EXTRA_TITLES: Readonly<Record<string, string>> = {
  "/configuracoes": "Configurações"
};

/** Título da página a partir do path atual (cabeçalho §4). */
export function pageTitleForPath(path: string): string {
  const exact = ALL_ITEMS.find((item) => item.path === path);
  if (exact !== undefined) {
    return exact.label;
  }
  if (EXTRA_TITLES[path] !== undefined) {
    return EXTRA_TITLES[path];
  }
  const prefixed = ALL_ITEMS.find(
    (item) => item.path !== "/" && path.startsWith(item.path)
  );
  return prefixed?.label ?? "FiCC";
}
